import {
  useCreatePeerNotification,
  useDeletePeerNotification,
  useGetPeerNotification,
} from '@givebacks/api/notification';
import { useFormListValidation } from 'core/Hooks';
import { getErrorMessage_v2 } from 'core/Utils';
import { Modal, notifications } from 'ui';
import { UseFormReturnType } from 'ui/Form';

import {
  DonationNotificationsForm,
  DonationNotificationsFormType,
} from '../../../molecules/forms/EditPeerPage/DonationNotificationsForm';
import { EditPeerPageModalMeta } from './EditPeerPage.types';
import { EditPeerPageModalFooter } from './EditPeerPageModalFooter';
import {
  ModalTitleWithBackButton,
  useEditPeerPageModalProvider,
} from './EditPeerPageModalProvider';

const EditDonationNotificationsModalFooter = ({
  onClose,
  meta,
}: {
  onClose: () => void;
  meta: EditPeerPageModalMeta;
}) => {
  const { data: notification } = useGetPeerNotification({
    causeId: meta.campaign.cause_id,
    peerId: meta.peer.uuid,
    peerType: meta.peer.__type,
  });

  const { mutate: createPeerNotification } = useCreatePeerNotification({
    peerType: meta.peer.__type,
  });
  const { mutate: deletePeerNotification } = useDeletePeerNotification();

  const { useGetForm } = useFormListValidation<{
    donationNotifications: UseFormReturnType<DonationNotificationsFormType>;
  }>();

  const donationNotifications = useGetForm('donationNotifications');

  const participantNotificationRequest = {
    notification: {
      store_item_id: meta.campaign.uuid,
      participant_id: meta.peer.uuid,
    },
  };

  const teamNotificationRequest = {
    notification: {
      store_item_id: meta.campaign.uuid,
      team_id: meta.peer.uuid,
    },
  };

  const notificationRequest =
    meta.peer.__type === 'participants' ? participantNotificationRequest : teamNotificationRequest;

  const handleSave = () => {
    const hasNotifications = !!notification;

    if (donationNotifications?.values?.notifications === undefined) {
      onClose();
    }

    if (donationNotifications.values.notifications === 'true') {
      // If the value is already the same, we don't need to do anything
      if (hasNotifications) {
        onClose();
      }
      createPeerNotification(
        {
          causeId: meta.campaign.cause_id,
          ...notificationRequest,
        },
        {
          onSuccess: () => {
            notifications.show({
              variant: 'success',
              message: 'Donation notifications settings updated',
            });
            onClose();
          },
          onError: (e) => {
            notifications.show({ variant: 'error', message: getErrorMessage_v2(e) });
          },
        }
      );
    } else if (donationNotifications.values.notifications === 'false') {
      // If the value is already the same, we don't need to do anything
      if (!hasNotifications) {
        onClose();
      }

      deletePeerNotification(
        {
          causeId: meta.campaign.cause_id,
          peerId: meta.peer.uuid,
          peerType: meta.peer.__type,
        },
        {
          onSuccess: () => {
            notifications.show({
              variant: 'success',
              message: 'Donation notifications deleted',
            });
            onClose();
          },
          onError: (e) => {
            notifications.show({ variant: 'error', message: getErrorMessage_v2(e) });
          },
        }
      );
    }
  };
  return <EditPeerPageModalFooter onClick={handleSave} />;
};

const EditDonationNotificationsModal = () => {
  const { opened, close, meta } = useEditPeerPageModalProvider({
    id: 'notifications',
  });

  return (
    <Modal
      opened={opened}
      onClose={close}
      title={<ModalTitleWithBackButton>Donation Notifications</ModalTitleWithBackButton>}
    >
      <DonationNotificationsForm
        participantId={meta.peer.uuid}
        causeId={meta.campaign.cause_id}
        peerType={meta.peer.__type}
      />
      <EditDonationNotificationsModalFooter onClose={close} meta={meta} />
    </Modal>
  );
};

export { EditDonationNotificationsModal };
